<template>
  <!-- ★ v29.31：容器由「全屏弹窗」改为「右侧抽屉」——配置面板不遮全屏、可与父级视图配置弹窗对照；
       内容布局与交互不变（内部滚动、底部操作区固定不随滚动），见文件底部 .csc-drawer 全局样式。 -->
  <a-drawer
    :title="`高级搜索组件 —— ${record?.title || record?.data_index || ''}`"
    placement="right"
    :width="SEARCH_DRAWER_WIDTH"
    root-class-name="csc-drawer"
    :open="open"
    destroy-on-close
    @close="handleCancel"
  >
    <!-- 统一布局骨架：纵向 flex（tip / tabs / footer 各占固定或弹性高度），
         纵向滚动只发生在 tabs 的 content-holder，Tab 标签栏与底部操作区固定不随滚动。
         v29.21：基础+高级合并为「属性配置」单页；「其他属性(JSON)」升级为「JSON配置」tab（CodeMirror 编辑整体 JSON） -->
    <a-form
      class="csc-body"
      :label-col="{ style: { width: '190px' } }"
    >
      <div class="adv-tip">
        「属性配置」= 基础 + 高级属性（按组件类型联动）；远程类组件在「数据源」页配置接口、显示字段、查询条件，
        本地静态选项类（下拉选框/单选框/多选框/分组下拉）在「数据源」页配置「选项列表」；
        「JSON配置」可直接编辑整体配置对象（与表单双向同步，保存前校验合法性）。
        运算符不在此配置——后端按组件类型自动补默认集。
      </div>

      <a-tabs
        v-model:activeKey="activeTab"
        class="csc-tabs"
        type="card"
        size="small"
      >
      <!-- ═══════════════ 属性配置（基础 + 高级合并单页） ═══════════════ -->
      <a-tab-pane
        key="props"
        tab="属性配置"
      >
        <BasicConfig
          :config="config"
          :record="record"
          @type-change="onTypeChange"
        />
        <template v-if="config.type">
          <a-divider
            class="props-divider"
            orientation="left"
            plain
          >
            高级属性
          </a-divider>
          <TextInputSetting
            v-if="config.type === 'input'"
            :config="config"
          />
          <NumberInputSetting
            v-else-if="NUMBER_TYPES.includes(config.type)"
            :config="config"
          />
          <FlatSelectSetting
            v-else-if="FLAT_SETTING_TYPES.includes(config.type)"
            :config="config"
          />
          <TreeSelectSetting
            v-else-if="['treeselect', 'cascader'].includes(config.type)"
            :config="config"
          />
          <DateSelectSetting
            v-else-if="DATE_TYPES.includes(config.type)"
            :config="config"
          />
          <RemoteSelectSetting
            v-else-if="REMOTE_TYPES.includes(config.type)"
            :config="config"
          />
        </template>
      </a-tab-pane>

      <!-- ═══════════════ 数据源（远程类调接口 / 本地静态选项类配选项列表，共享 DataSourcePanel） ═══════════════ -->
      <a-tab-pane
        key="ds"
        tab="数据源"
        :disabled="!dsTabEnabled"
      >
        <DataSourcePanel
          v-if="dsTabEnabled"
          v-model:init-model-text="initModelText"
          :config="config"
          :record="record"
        />
      </a-tab-pane>

      <!-- ═══════════════ JSON 配置（CodeMirror 编辑整体配置对象） ═══════════════ -->
      <a-tab-pane
        key="json"
        tab="JSON配置"
        :disabled="!config.type"
      >
        <div class="csc-json-pane">
          <div class="json-toolbar">
            <a-alert
              v-if="jsonError"
              type="error"
              show-icon
              :message="jsonError"
              banner
            />
            <span
              v-else
              class="json-tip"
            >整体配置对象，与「属性配置」表单实时双向同步；保存时以此为最终值。</span>
            <a-button
              size="small"
              :disabled="!!jsonError"
              @click="handleFormatJson"
            >
              格式化
            </a-button>
          </div>
          <div class="json-editor-wrap">
            <CodeEditor
              ref="jsonEditorRef"
              v-model:editorValue="jsonText"
              mode="javascript"
              :options="{ lineNumbers: true, indentUnit: 2, lineWrapping: false }"
            />
          </div>
        </div>
      </a-tab-pane>
    </a-tabs>

    <div
      v-if="config.type"
      class="adv-footer"
    >
      <a-button
        size="small"
        danger
        @click="handleClear"
      >
        清除配置（恢复按类型自动推导）
      </a-button>
    </div>
    </a-form>

    <!-- 抽屉无 cancel-text/ok-text 属性（那是 a-modal 的），操作区用 footer 插槽 -->
    <template #footer>
      <div class="csc-drawer-footer">
        <a-button @click="handleCancel">
          取消
        </a-button>
        <a-button
          type="primary"
          @click="handleOk"
        >
          确定
        </a-button>
      </div>
    </template>
  </a-drawer>
</template>

<script setup lang="ts">
import { message } from 'ant-design-vue';
import { cloneDeep } from 'lodash';
import { computed, nextTick, ref, watch } from 'vue';
import CodeEditor from 'liyu-pc-base/components/code/CodeEditor.vue';
import {
  DATE_TYPES,
  FLAT_SETTING_TYPES,
  LOCAL_OPTION_TYPES,
  NUMBER_TYPES,
  RANGE_PLACEHOLDER_TYPES,
  REMOTE_TYPES,
  TYPE_DEFAULTS,
  hasOptionValue,
} from './searchComponentSetting/constants';
import BasicConfig from './searchComponentSetting/BasicConfig.vue';
import DataSourcePanel from './searchComponentSetting/DataSourcePanel.vue';
import DateSelectSetting from './searchComponentSetting/settings/DateSelectSetting.vue';
import FlatSelectSetting from './searchComponentSetting/settings/FlatSelectSetting.vue';
import NumberInputSetting from './searchComponentSetting/settings/NumberInputSetting.vue';
import RemoteSelectSetting from './searchComponentSetting/settings/RemoteSelectSetting.vue';
import TextInputSetting from './searchComponentSetting/settings/TextInputSetting.vue';
import TreeSelectSetting from './searchComponentSetting/settings/TreeSelectSetting.vue';

/* ★ v29.19 完整移植 FieldSetting.vue 搜索组件配置；v29.20 拆分 + 全屏弹窗；v29.31 改为右侧抽屉：
   - 外壳只负责容器编排（tab 分发 / 类型切换合并默认值 / 确定保存与 JSON 合并清理）；
   - 基础属性 = BasicConfig（共享）；高级属性按组件类型分发到 settings/*（组合共享基础配置块）；
   - 数据源页 = DataSourcePanel（远程五类专属，含查询参数编辑器 QueryParamsConfig）。
   产出物 = 完整组件配置对象（JSONField advanced_search_component），
   get_page_config 编译时整体并入高级搜索组件对象（field_name 由列锁定）。 */

/* 抽屉宽度：内容含 190px 的标签列 + 远程数据源（URL/字段映射/查询参数） + CodeMirror JSON 编辑器，
   过窄会把控件挤成多行。父级「视图配置」弹窗为 1020px，这里取略窄以保证「抽屉」观感；
   要调宽度只改这一处。 */
const SEARCH_DRAWER_WIDTH = 920;

interface Props {
  open: boolean;
  record: any; // 列配置行（读写其 advanced_search_component）
}
const props = defineProps<Props>();
const open = defineModel<boolean>('open', { default: false });

const isRemote = computed(() => REMOTE_TYPES.includes(config.value?.type));
/* ★ v29.29：「数据源」tab 的启用范围 = 远程五类（接口/字段映射） + 本地静态选项类（选项列表）。
   此前只看 isRemote，导致 select/radio/checkbox 的「选项列表」（原版 FieldSetting 就挂在数据源页）
   既没有入口也进不去那一页 → 选框类组件配不出选项。 */
const dsTabEnabled = computed(
  () => isRemote.value || LOCAL_OPTION_TYPES.includes(config.value?.type),
);

/* ───────── 状态 ───────── */
const activeTab = ref('props');
const config = ref<any>({});
const initModelText = ref('');
/* JSON 配置 tab：jsonDirty=编辑器文本领先于 config（未同步/语法错误）；jsonError=当前文本非法 */
const jsonText = ref('');
const jsonDirty = ref(false);
const jsonError = ref('');
const jsonEditorRef = ref<any>();

/* ───────── 打开时初始化 ───────── */
watch(
  () => props.open,
  (val) => {
    if (!val) return;
    activeTab.value = 'props';
    const saved = props.record?.advanced_search_component;
    /* config 恒为对象（v29.18 null 崩溃修复沿用）；保存过的配置 + 类型默认值合并（保存值优先） */
    if (saved && typeof saved === 'object' && saved.type) {
      config.value = normalizePlaceholder({ ...cloneDeep(TYPE_DEFAULTS[saved.type] || {}), ...cloneDeep(saved) });
      config.value.type = saved.type;
    } else {
      config.value = {};
    }
    initModelText.value = config.value.init_model ? JSON.stringify(config.value.init_model, null, 2) : '';
    jsonText.value = '';
    jsonDirty.value = false;
    jsonError.value = '';
  },
);

/* ───────── JSON 配置 tab：配置实时镜像 + 编辑文本回写 ───────── */
/* 序列化快照：config + 数据源页未保存的 init_model（initModelText 独立文本态，保存时才合并），
   保证 JSON 页看到/保存的语义与 handleOk 一致 */
const buildSnapshot = () => {
  const snapshot = cloneDeep(config.value);
  if (initModelText.value.trim()) {
    try {
      snapshot.init_model = JSON.parse(initModelText.value);
    } catch {
      /* 数据源页文本非法：不并入，由其自身保存校验拦截 */
    }
  } else {
    delete snapshot.init_model;
  }
  return snapshot;
};

watch(activeTab, (tab, oldTab) => {
  /* 离开 JSON 页时仍有语法错误：提示未同步（是否丢弃取决于期间是否改过表单，见下方镜像守卫） */
  if (oldTab === 'json' && jsonError.value) {
    message.warning('JSON 配置存在语法错误，未同步到表单');
  }
  /* 进入时兜底刷新：覆盖「只改了 initModelText 而 config 未变」（镜像 watch 不会触发）的情况 */
  if (tab === 'json' && !jsonDirty.value) {
    jsonText.value = JSON.stringify(buildSnapshot(), null, 2);
    jsonError.value = '';
  }
  if (tab === 'json') {
    /* CodeMirror 可能在 pane 隐藏期间收到过 setValue，重新显示后主动 refresh，防止渲染滞后 */
    nextTick(() => jsonEditorRef.value?.handleCodeMirrorRefresh?.());
  }
});

/* ★ 配置实时镜像：任何 config 变化（表单/数据源页/类型切换）立即刷新 JSON 文本并清除脏态，
   不再依赖「进入 tab」时机。说明：
   - config 变化只可能来自非 JSON 页（JSON 页内无法编辑表单），无条件覆盖即「表单为准」，
     非法草稿随表单编辑作废（离开时已有 warning 提示）；
   - JSON 页内合法回写也会触发本 watch，但镜像文本与编辑器现有文本一致，自然收敛不循环。 */
watch(
  config,
  () => {
    if (config.value?.type) {
      jsonDirty.value = false;
      jsonText.value = JSON.stringify(buildSnapshot(), null, 2);
    }
  },
  { deep: true },
);

watch(jsonText, (text) => {
  if (!text.trim()) {
    jsonError.value = '';
    return;
  }
  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) || !parsed.type) {
      jsonError.value = '必须是包含 type 字符串键的 JSON 对象（如 {"type":"input", ...}），未同步到表单';
      return;
    }
    /* 与打开初始化/类型切换同一语义：JSON 为准，缺键补类型默认（cloneDeep 防共享 TYPE_DEFAULTS 引用），
       再归一 placeholder 形状 */
    config.value = normalizePlaceholder({ ...cloneDeep(TYPE_DEFAULTS[parsed.type] || {}), ...parsed });
    /* init_model 在 DataSourcePanel 有独立文本态，保持一致避免保存时被旧值覆盖 */
    initModelText.value = config.value.init_model ? JSON.stringify(config.value.init_model, null, 2) : '';
    jsonError.value = '';
    jsonDirty.value = false;
  } catch (e: any) {
    jsonError.value = `JSON 语法错误：${e?.message || e}（未同步到表单）`;
    jsonDirty.value = true;
  }
});

const handleFormatJson = () => {
  try {
    jsonText.value = JSON.stringify(JSON.parse(jsonText.value), null, 2);
  } catch {
    /* 语法错误时按钮已禁用，防御性兜底 */
  }
};

/* ───────── placeholder 形状归一（区间类=数组双输入，其余=字符串） ─────────
   覆盖三个 config 入口：打开初始化 / 类型切换（keep 跨类保留）/ JSON 回写（直改 type 或缺键），
   否则 BasicConfig 区间分支绑定 config.placeholder[0] 会在 undefined/字符串上崩溃或赋值无效 */
const normalizePlaceholder = (cfg: any) => {
  if (!cfg?.type) return cfg;
  const isRange = RANGE_PLACEHOLDER_TYPES.includes(cfg.type);
  if (isRange) {
    if (!Array.isArray(cfg.placeholder)) {
      cfg.placeholder =
        typeof cfg.placeholder === 'string' && cfg.placeholder
          ? [cfg.placeholder, cfg.placeholder]
          : cloneDeep(TYPE_DEFAULTS[cfg.type]?.placeholder) || ['开始', '结束'];
    }
  } else if (Array.isArray(cfg.placeholder)) {
    cfg.placeholder = cfg.placeholder.filter(Boolean)[0] || '';
  } else if (typeof cfg.placeholder !== 'string') {
    cfg.placeholder = TYPE_DEFAULTS[cfg.type]?.placeholder ?? '';
  }
  return cfg;
};

/* ───────── 类型切换 ───────── */
const onTypeChange = (type: string) => {
  if (!type) {
    config.value = {};
    return;
  }
  const prev = config.value || {};
  // 切类型：以该类型默认属性为基础，保留通用已填项（label/placeholder/allowClear/disabled）
  const keep: any = {};
  if (prev.label) keep.label = prev.label;
  if (prev.disabled != null) keep.disabled = prev.disabled;
  if (RANGE_PLACEHOLDER_TYPES.includes(prev.type)) {
    if (Array.isArray(prev.placeholder)) keep.placeholder = prev.placeholder;
  } else if (typeof prev.placeholder === 'string') {
    keep.placeholder = prev.placeholder;
  }
  if (prev.allowClear != null) keep.allowClear = prev.allowClear;
  config.value = normalizePlaceholder({ ...cloneDeep(TYPE_DEFAULTS[type] || { type }), ...keep });
  /* 类型变了，JSON tab 下次进入需按新配置重新序列化 */
  jsonDirty.value = false;
};

/* ───────── 确定保存 ───────── */
const handleClear = () => {
  props.record.advanced_search_component = null;
  config.value = {};
  open.value = false;
};

const handleOk = () => {
  if (!config.value?.type) {
    // 未选类型 = 不配置（保留原值不动）
    open.value = false;
    return;
  }
  if (jsonError.value) {
    message.warning('JSON 配置存在语法错误，请修正后再保存');
    return;
  }
  const finalConfig = cloneDeep(config.value);
  if (initModelText.value.trim()) {
    try {
      finalConfig.init_model = JSON.parse(initModelText.value.trim());
    } catch {
      message.warning('初始参数不是合法 JSON，请修正后再保存');
      return;
    }
  } else {
    delete finalConfig.init_model;
  }
  /* ★ v29.29：本地静态选项类（select/radio/checkbox/groupselect）
     ① 选项对象必须是 lybase choices 契约 `{value, label}`，且不能带 valueKey/labelKey ——
        运行时按 `option[labelKey||'label']` 取显示文本，残留 `labelKey:'name'` 会整列渲染成空白；
     ② 至少一个有效选项：这类组件没有别的 options 来源（远程接口那套对它们不适用），
        空选项落库 = 一个永远选不出东西的下拉框，属静默坏配置，这里直接拦住。 */
  if (LOCAL_OPTION_TYPES.includes(finalConfig.type)) {
    delete finalConfig.valueKey;
    delete finalConfig.labelKey;
    /* 只丢「整行空白」的项；手写在 JSON 里的**嵌套分组节点**（antdv `{label, options:[...]}`，
       逐项直传 groupselect）自身没有 value，必须原样保留，否则一次保存就被清空。 */
    const opts = (Array.isArray(finalConfig.options) ? finalConfig.options : []).filter(
      (o: any) => hasOptionValue(o) || (Array.isArray(o?.options) && o.options.length > 0),
    );
    if (opts.length === 0) {
      message.warning('下拉选框/单选框/多选框至少需要一个选项，请在「数据源」页的「选项列表」中添加');
      return;
    }
    finalConfig.options = opts;
  }
  // 清理空壳键：空数组/空串不落库
  ['labelKeyList', 'extendedFields', 'searchConditions', 'simpleSearchConditions', 'required_condition', 'options'].forEach((k) => {
    if (Array.isArray(finalConfig[k]) && finalConfig[k].length === 0) delete finalConfig[k];
  });
  if (finalConfig.defaultValueType === '') delete finalConfig.defaultValueType;
  props.record.advanced_search_component = finalConfig;
  jsonDirty.value = false;
  open.value = false;
};
const handleCancel = () => {
  open.value = false;
};
</script>

<style scoped lang="less">
.csc-body {
  /* 抽屉 body 与改造前的弹窗 body 一样是「确定高度的 flex item」，flex:1 + height:100% 双保险撑满剩余高度 */
  flex: 1;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.adv-tip {
  flex: none;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 12px;
  line-height: 1.5;
}
.csc-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* 滚动只发生在内容区：Tab 标签栏固定 */
  :deep(.ant-tabs-nav) {
    flex: none;
    margin-bottom: 12px;
  }
  :deep(.ant-tabs-content-holder) {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    /* 抵消 a-row gutter 的 -8px 负 margin，消除多余横向滚动条 */
    padding: 0 8px;
  }
  /* 让 tabpane 拿到确定高度，JSON 编辑器才能撑满内容区 */
  :deep(.ant-tabs-content),
  :deep(.ant-tabs-tabpane) {
    height: 100%;
  }
}
.props-divider {
  margin: 8px 0 12px;
}
.csc-json-pane {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.json-toolbar {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  min-width: 0;
}
.json-toolbar .ant-alert {
  flex: 1;
  min-width: 0;
}
.json-tip {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1.5;
}
.json-editor-wrap {
  flex: 1;
  min-height: 0;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
}
/* CodeEditor 全局样式固定 300px 高，这里撑满 JSON 页剩余高度 */
.json-editor-wrap :deep(.CodeMirror) {
  height: 100% !important;
  font-size: 13px !important;
}
.adv-footer {
  flex: none;
  margin-top: 8px;
}
/* 抽屉底部操作区（footer 插槽内容，是本文档的元素 → scoped 可命中） */
.csc-drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>

<!-- ★ v29.31：抽屉是 teleport 到 body 的，`root-class-name` 挂在**抽屉自己的根节点**上，
     本组件根元素不是它的祖先 → scoped 的 :deep() 选不中，只能写全局样式（用 .csc-drawer 限定作用域）。
     ⚠️ antdv 4.2.6 的 a-drawer **没有 `wrap-class-name`**（只有 `root-class-name`/`root-style`），
     别照搬 a-modal 的 wrap-class-name 写法 —— 会被当成普通 attr 落到别处，样式静默失效。
     ⚠️ 选择器**刻意写成三段** `.csc-drawer.ant-drawer .ant-drawer-body`（特异度 0,3,0）：
     antd 的 `.ant-drawer-body` 规则经 cssinjs 生成后是 `:where(.css-hash).ant-drawer .ant-drawer-body`（0,2,0），
     两段式 `.csc-drawer .ant-drawer-body`（同为 0,2,0）会与它**打平**，胜负交给样式注入顺序 —— 不稳。
     （`.csc-drawer` 与 `.ant-drawer` 同在抽屉根节点上：vc-drawer 把 rootClassName 并进根节点的 class 列表。） -->
<style lang="less">
.csc-drawer.ant-drawer {
  /* 内部滚动：滚动只发生在 tabs 的 content-holder（见上方 scoped 块），
     抽屉标题栏与底部操作区固定不随滚动 —— 与改造前的全屏弹窗行为一致 */
  .ant-drawer-body {
    display: flex;
    flex-direction: column;
    padding: 16px;
    overflow: hidden;
  }
  .ant-drawer-footer {
    flex: none;
  }
}
</style>
